import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText
} from '@mui/material';
import { SaveAlt, Refresh, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';

const SpecialPrice = () => {
  const [barcode, setBarcode] = useState('');
  const [price, setPrice] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [specialPrices, setSpecialPrices] = useState([]);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editPrice, setEditPrice] = useState('');
  const [editLoading, setEditLoading] = useState(false);

  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingProduct, setDeletingProduct] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const apiBaseUrl = 'https://api.prixora.lt/api/ProductProcessing';

  const fetchSpecialPrices = async () => {
    setFetchLoading(true);
    setError('');

    try {
      const response = await fetch(`${apiBaseUrl}/special-pricing`, {
        method: 'GET',
        headers: {
          'accept': '*/*'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSpecialPrices(data.products || []);
        setTotalCount(data.count || 0);
      } else {
        throw new Error(`HTTP klaida! statusas: ${response.status}`);
      }
    } catch (err) {
      setError('Nepavyko gauti specialių kainų sąrašo: ' + err.message);
      setSpecialPrices([]);
    } finally {
      setFetchLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate inputs
    if (!barcode.trim()) {
      setError('Prašome įvesti brūkšninį kodą');
      return;
    }

    if (!price || parseFloat(price) <= 0) {
      setError('Prašome įvesti teisingą kainą');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${apiBaseUrl}/special-pricing`, {
        method: 'POST',
        headers: {
          'accept': '*/*',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          barcode: barcode.trim(),
          marketplace: 'varle',
          price: parseFloat(price)
        })
      });

      if (response.ok) {
        setSuccess('Speciali kaina sėkmingai nustatyta!');
        // Clear form after success
        setBarcode('');
        setPrice('');
        // Refresh the list
        await fetchSpecialPrices();
      } else {
        const errorData = await response.text();
        throw new Error(`HTTP klaida! statusas: ${response.status}, žinutė: ${errorData}`);
      }
    } catch (err) {
      setError('Nepavyko nustatyti specialios kainos: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleBarcodeChange = (e) => {
    setBarcode(e.target.value);
    setError('');
    setSuccess('');
  };

  const handlePriceChange = (e) => {
    setPrice(e.target.value);
    setError('');
    setSuccess('');
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('lt-LT');
  };

  // Edit handlers
  const handleEditClick = (product) => {
    setEditingProduct(product);
    setEditPrice(product.varle?.price?.toString() || '');
    setEditDialogOpen(true);
    setError('');
    setSuccess('');
  };

  const handleEditClose = () => {
    setEditDialogOpen(false);
    setEditingProduct(null);
    setEditPrice('');
  };

  const handleEditSubmit = async () => {
  if (!editPrice || parseFloat(editPrice) <= 0) {
    setError('Prašome įvesti teisingą kainą');
    return;
  }

  setEditLoading(true);
  setError('');
  setSuccess('');

  try {
    const response = await fetch(`${apiBaseUrl}/special-pricing`, {
      method: 'PUT',  // Changed from POST to PUT
      headers: {
        'accept': '*/*',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        barcode: editingProduct.barcode,
        marketplace: 'varle',
        price: parseFloat(editPrice)
      })
    });

    if (response.ok) {
      setSuccess('Kaina sėkmingai atnaujinta!');
      handleEditClose();
      await fetchSpecialPrices();
    } else {
      const errorData = await response.text();
      throw new Error(`HTTP klaida! statusas: ${response.status}, žinutė: ${errorData}`);
    }
  } catch (err) {
    setError('Nepavyko atnaujinti kainos: ' + err.message);
  } finally {
    setEditLoading(false);
  }
};

  // Delete handlers
  const handleDeleteClick = (product) => {
    setDeletingProduct(product);
    setDeleteDialogOpen(true);
    setError('');
    setSuccess('');
  };

  const handleDeleteClose = () => {
    setDeleteDialogOpen(false);
    setDeletingProduct(null);
  };

  const handleDeleteConfirm = async () => {
    setDeleteLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${apiBaseUrl}/special-pricing`, {
        method: 'DELETE',
        headers: {
          'accept': '*/*',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          barcode: deletingProduct.barcode,
          marketplace: 'varle'
        })
      });

      if (response.ok) {
        setSuccess('Speciali kaina sėkmingai pašalinta!');
        handleDeleteClose();
        await fetchSpecialPrices();
      } else {
        const errorData = await response.text();
        throw new Error(`HTTP klaida! statusas: ${response.status}, žinutė: ${errorData}`);
      }
    } catch (err) {
      setError('Nepavyko pašalinti specialios kainos: ' + err.message);
    } finally {
      setDeleteLoading(false);
    }
  };

  useEffect(() => {
    fetchSpecialPrices();
  }, []);

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            Nustatyti Specialią Kainą
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Nustatykite specialią kainą produktui Varle rinkos vietoje
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {success}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Brūkšninis kodas"
            variant="outlined"
            value={barcode}
            onChange={handleBarcodeChange}
            placeholder="Įveskite produkto brūkšninį kodą"
            required
            sx={{ mb: 3 }}
            disabled={loading}
          />

          <TextField
            fullWidth
            label="Speciali kaina"
            variant="outlined"
            type="number"
            value={price}
            onChange={handlePriceChange}
            placeholder="0.00"
            required
            inputProps={{
              min: 0,
              step: 0.01
            }}
            InputProps={{
              startAdornment: <InputAdornment position="start">€</InputAdornment>,
            }}
            sx={{ mb: 3 }}
            disabled={loading}
          />

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              type="submit"
              variant="contained"
              startIcon={loading ? <CircularProgress size={20} /> : <SaveAlt />}
              disabled={loading}
              fullWidth
              size="large"
            >
              {loading ? 'Siunčiama...' : 'Nustatyti Kainą'}
            </Button>
          </Box>
        </Box>

        <Box sx={{ mt: 4, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary">
            <strong>Pastaba:</strong> Ši forma nustato specialią kainą konkrečiam produktui Varle rinkos vietoje.
            Įveskite produkto brūkšninį kodą ir norimą specialią kainą eurais.
          </Typography>
        </Box>
      </Paper>

      <Paper elevation={3} sx={{ p: 4, mt: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Box>
            <Typography variant="h5" component="h2" gutterBottom>
              Produktai su Specialiomis Kainomis
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Produktai, kuriems nustatytos specialios kainos Varle rinkos vietoje
            </Typography>
            {totalCount > 0 && (
              <Chip
                label={`Iš viso: ${totalCount}`}
                color="primary"
                sx={{ mt: 1 }}
              />
            )}
          </Box>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchSpecialPrices}
            disabled={fetchLoading}
          >
            Atnaujinti
          </Button>
        </Box>

        {fetchLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper} elevation={1}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Brūkšninis kodas</strong></TableCell>
                  <TableCell><strong>Produkto pavadinimas</strong></TableCell>
                  <TableCell><strong>Varle kaina</strong></TableCell>
                  <TableCell><strong>Statusas</strong></TableCell>
                  <TableCell><strong>Atnaujinta</strong></TableCell>
                  <TableCell align="center"><strong>Veiksmai</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {specialPrices.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography color="text.secondary">
                        Produktų su specialiomis kainomis nerasta
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  specialPrices.map((product, index) => (
                    <TableRow key={index} hover>
                      <TableCell>
                        <Typography variant="body1" fontFamily="monospace">
                          {product.barcode}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {product.productName || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body1" fontWeight="bold" color="primary">
                          €{product.varle?.price?.toFixed(2) || '0.00'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1, flexDirection: 'column' }}>
                          {product.varle?.isActive && (
                            <Chip label="Aktyvus" color="success" size="small" />
                          )}
                          {product.varle?.isExcluded && (
                            <Chip label="Išimtas" color="warning" size="small" />
                          )}
                          {!product.varle?.isActive && !product.varle?.isExcluded && (
                            <Chip label="Neaktyvus" color="default" size="small" />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {formatDate(product.updatedAt)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          <IconButton
                            color="primary"
                            size="small"
                            onClick={() => handleEditClick(product)}
                            title="Redaguoti kainą"
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            color="error"
                            size="small"
                            onClick={() => handleDeleteClick(product)}
                            title="Pašalinti specialią kainą"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={handleEditClose} maxWidth="sm" fullWidth>
        <DialogTitle>Redaguoti Specialią Kainą</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Redaguojamas produktas: <strong>{editingProduct?.barcode}</strong>
            <br />
            {editingProduct?.productName}
          </DialogContentText>
          <TextField
            fullWidth
            label="Nauja speciali kaina"
            variant="outlined"
            type="number"
            value={editPrice}
            onChange={(e) => setEditPrice(e.target.value)}
            placeholder="0.00"
            required
            autoFocus
            inputProps={{
              min: 0,
              step: 0.01
            }}
            InputProps={{
              startAdornment: <InputAdornment position="start">€</InputAdornment>,
            }}
            sx={{ mt: 2 }}
            disabled={editLoading}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditClose} disabled={editLoading}>
            Atšaukti
          </Button>
          <Button
            onClick={handleEditSubmit}
            variant="contained"
            disabled={editLoading}
            startIcon={editLoading ? <CircularProgress size={20} /> : null}
          >
            {editLoading ? 'Saugoma...' : 'Išsaugoti'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteClose} maxWidth="sm" fullWidth>
        <DialogTitle>Pašalinti Specialią Kainą</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Ar tikrai norite pašalinti specialią kainą šiam produktui?
            <br /><br />
            <strong>Brūkšninis kodas:</strong> {deletingProduct?.barcode}
            <br />
            <strong>Produktas:</strong> {deletingProduct?.productName}
            <br />
            <strong>Dabartinė kaina:</strong> €{deletingProduct?.varle?.price?.toFixed(2)}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteClose} disabled={deleteLoading}>
            Atšaukti
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            disabled={deleteLoading}
            startIcon={deleteLoading ? <CircularProgress size={20} /> : null}
          >
            {deleteLoading ? 'Šalinama...' : 'Pašalinti'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default SpecialPrice;
